import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Briefcase, ShieldCheck, GraduationCap, Search, Award, Sparkles, TrendingUp, Scale, Globe2, Heart, CheckCircle2 } from "lucide-react";

const cosaFacciamo = [
  { icon: Search, title: "Audit di accessibilità", text: "Valutazione di sedi, punti vendita, siti produttivi e servizi al pubblico secondo standard nazionali e Universal Design." },
  { icon: ShieldCheck, title: "Compliance normativa", text: "Supporto su DM 236/89, DPR 503/96, EAA (European Accessibility Act 2025) e Direttiva (UE) 2019/882." },
  { icon: GraduationCap, title: "Formazione aziendale", text: "Corsi su disability management, accoglienza inclusiva e progettazione accessibile per HR, retail e customer care." },
  { icon: Award, title: "Bollino FIABA", text: "Riconoscimento alle aziende che dimostrano impegno concreto su accessibilità e inclusione." },
];

const beneficiSteps = [
  "Riduzione del rischio legale e reputazionale legato alla non conformità",
  "Ampliamento del bacino clienti: in Italia oltre 13 milioni di persone con disabilità o ridotta mobilità",
  "Valorizzazione ESG e indicatori di sostenibilità sociale (rating S)",
  "Vantaggio competitivo nei bandi pubblici e nelle gare con criteri sociali",
  "Welfare interno: ambienti di lavoro più inclusivi per dipendenti e visitatori",
  "Comunicazione autentica: certificazione di terza parte indipendente",
];

const perche = [
  { icon: Sparkles, title: "Brand riconosciuto dal 2000", text: "FIABA è un marchio noto a Istituzioni, media e imprese: associare il vostro nome al nostro rafforza la credibilità." },
  { icon: Scale, title: "Terza parte indipendente", text: "Ente del Terzo Settore senza scopo di lucro: i nostri audit hanno valore di certificazione esterna oggettiva." },
  { icon: TrendingUp, title: "Impatto misurabile", text: "Ogni intervento è documentato con KPI utilizzabili nei bilanci di sostenibilità e nei report ESG." },
  { icon: Globe2, title: "Allineati a EAA 2025", text: "Vi prepariamo all'European Accessibility Act in vigore da giugno 2025: sanzioni evitate e mercato UE accessibile." },
  { icon: Heart, title: "Co-progettazione con utenti reali", text: "Lavoriamo con persone con disabilità nei test di prodotto e servizio: feedback autentico, non solo checklist." },
  { icon: Briefcase, title: "Esperienza enterprise", text: "Progetti realizzati con grandi gruppi industriali, infrastrutture, retail e PA: metodologie scalabili e rodate." },
];


const ServiziAziende = () => (
  <>
    <SEO
      title="Servizi per le Aziende — Audit & Bollino FIABA | FIABA ETS"
      description="Consulenza, audit di accessibilità, formazione e Bollino FIABA per imprese che vogliono essere conformi all'European Accessibility Act e realmente inclusive."
      path="/servizi-aziende"
    />
    <PageHero
      eyebrow="Servizi per le Imprese"
      title="L'accessibilità è un vantaggio competitivo"
      subtitle="Compliance, formazione e certificazione per aziende che mettono le persone al centro."
    />

    <section className="section-padding">
      <div className="container-narrow space-y-12">
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl font-bold text-primary mb-4">Cosa fa FIABA per le aziende</h2>
          <p className="text-foreground/80 leading-relaxed">
            Affianchiamo imprese di ogni dimensione nel percorso verso una piena accessibilità di prodotti,
            servizi, ambienti di lavoro e comunicazione. Un investimento che riduce i rischi normativi,
            allarga il mercato e rafforza il posizionamento ESG.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {cosaFacciamo.map((s) => (
            <div key={s.title} className="bg-card border border-border rounded-lg p-6 shadow-soft hover:shadow-elegant transition-shadow">
              <s.icon className="w-10 h-10 text-secondary mb-4" aria-hidden="true" />
              <h3 className="font-semibold text-lg text-primary mb-2">{s.title}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding bg-muted/40">
      <div className="container-narrow grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-4">
          <span className="inline-block bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">Perché sceglierci</span>
          <h2 className="font-serif text-3xl font-bold text-primary">I benefici concreti</h2>
          <p className="text-foreground/80 leading-relaxed">
            Lavorare con FIABA significa affidarsi a un Ente del Terzo Settore con 25 anni di esperienza,
            riconosciuto da Istituzioni nazionali ed europee. Ogni intervento è documentato, misurabile e
            spendibile nei report di sostenibilità.
          </p>
        </div>
        <div className="lg:col-span-3 bg-card border border-border rounded-lg p-7 shadow-elegant">
          <h3 className="font-semibold text-primary mb-5 flex items-center gap-2"><Briefcase className="w-5 h-5 text-secondary" /> Cosa otterrai</h3>
          <ul className="space-y-3">
            {beneficiSteps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-foreground/85">
                <CheckCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" aria-hidden="true" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    <section className="section-padding">
      <div className="container-narrow">
        <span className="inline-block bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4">Perché sceglierci</span>
        <h2 className="font-serif text-3xl font-bold text-primary mb-3">Quello che ci rende diversi</h2>
        <p className="text-foreground/70 mb-10 max-w-2xl">Sei ragioni concrete per scegliere FIABA come partner della vostra strategia di accessibilità e inclusione.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {perche.map((p) => (
            <div key={p.title} className="bg-card border border-border rounded-lg p-6 shadow-soft hover:shadow-elegant transition-shadow">
              <p.icon className="w-9 h-9 text-secondary mb-3" aria-hidden="true" />
              <h3 className="font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 bg-hero text-primary-foreground p-8 rounded-lg shadow-elegant flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <h3 className="font-serif text-2xl font-bold mb-2">Parliamone</h3>
            <p className="text-primary-foreground/90 text-sm">Richiedi un primo incontro conoscitivo gratuito con il nostro team.</p>
          </div>
          <Button asChild size="lg" className="bg-gold text-secondary-foreground hover:opacity-90 font-semibold">
            <Link to="/contatti">Contattaci</Link>
          </Button>
        </div>
      </div>
    </section>
  </>
);

export default ServiziAziende;
